import React, { useState, useCallback, useRef } from 'react';
import Icon from 'components/atoms/Icon';
import Text from 'components/atoms/Text';
import { COLOR_SYSTEM } from 'components/design/design-system';
import useSecureKeypad from 'components/hooks/useSecureKeypad';
import {
  Overlay,
  Backdrop,
  KeypadPanel,
  DisplayRow,
  Dot,
  KeyGrid,
  KeyButton,
  ActionButton,
} from './style';

interface SecureKeypadProps {
  maxLength: number;
  onComplete: (value: string) => void;
  onClose: () => void;
}

const SecureKeypad: React.FC<SecureKeypadProps> = ({ maxLength, onComplete, onClose }) => {
  const { value, keyLayout, handleKeyPress, handleDelete } = useSecureKeypad({
    maxLength,
    onComplete,
  });

  // 디코이: 실제 누른 키 외에 다른 키 하나도 동시에 pressed 효과
  const [decoyDigit, setDecoyDigit] = useState<number | null>(null);
  const decoyTimer = useRef<number | null>(null);

  const triggerDecoy = useCallback((pressedDigit: number) => {
    const others = keyLayout.filter((d) => d !== pressedDigit);
    const randomDecoy = others[Math.floor(Math.random() * others.length)];
    setDecoyDigit(randomDecoy);
    if (decoyTimer.current) clearTimeout(decoyTimer.current);
    decoyTimer.current = window.setTimeout(() => setDecoyDigit(null), 150);
  }, [keyLayout]);

  const handleKeyPressWithDecoy = useCallback((digit: number) => {
    triggerDecoy(digit);
    handleKeyPress(digit);
  }, [triggerDecoy, handleKeyPress]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay>
      <Backdrop onClick={handleBackdropClick} />
      <KeypadPanel>
        <Text
          textType="Body2"
          color={COLOR_SYSTEM.get('Gray')?.[500]}
          style={{ textAlign: 'center', marginBottom: '12px' }}
        >
          뒤 7자리를 입력하세요
        </Text>

        <DisplayRow>
          {Array.from({ length: maxLength }, (_, i) => (
            <Dot key={i} $filled={i < value.length} />
          ))}
        </DisplayRow>

        <KeyGrid>
          {keyLayout.slice(0, 9).map((digit) => (
            <KeyButton
              key={digit}
              type="button"
              onClick={() => handleKeyPressWithDecoy(digit)}
              $decoyActive={decoyDigit === digit}
            >
              {digit}
            </KeyButton>
          ))}

          <ActionButton type="button" $variant="delete" onClick={handleDelete}>
            <Icon name="Back" size={24} color={COLOR_SYSTEM.get('Gray')?.[600]} />
          </ActionButton>

          <KeyButton
            type="button"
            onClick={() => handleKeyPressWithDecoy(keyLayout[9])}
            $decoyActive={decoyDigit === keyLayout[9]}
          >
            {keyLayout[9]}
          </KeyButton>

          <ActionButton
            type="button"
            $variant="confirm"
            onClick={() => {
              if (value.length === maxLength) onComplete(value);
            }}
          >
            완료
          </ActionButton>
        </KeyGrid>
      </KeypadPanel>
    </Overlay>
  );
};

export default SecureKeypad;
